import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-200 ease-in-out disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/50 aria-invalid:ring-destructive/20 aria-invalid:border-destructive transform active:scale-95",
  {
    defaultVariants: {
      size: "default",
      variant: "default",
    },
    variants: {
      size: {
        icon: "size-10 rounded-lg",
        default: "h-10 px-5 py-2.5 has-[>svg]:px-4",
        lg: "h-12 rounded-lg px-8 has-[>svg]:px-6 text-base",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5 text-xs",
      },
      variant: {
        link: "text-primary underline-offset-4 hover:underline hover:text-primary/80",
        ghost:
          "hover:bg-accent/10 hover:text-accent-foreground hover:shadow-sm",
        secondary:
          "bg-secondary text-secondary-foreground shadow-md hover:bg-secondary/90 hover:shadow-lg focus-visible:shadow-lg",
        outline:
          "border-2 border-primary/20 bg-transparent shadow-sm hover:bg-primary/5 hover:border-primary/30 hover:shadow-md hover:text-accent-foreground",
        default:
          "bg-gradient-to-r from-primary to-primary/90 text-primary-foreground shadow-lg hover:shadow-xl hover:from-primary/90 hover:to-primary/80 focus-visible:shadow-xl",
        destructive:
          "bg-gradient-to-r from-destructive to-destructive/90 text-white shadow-lg hover:shadow-xl hover:from-destructive/90 hover:to-destructive/80 focus-visible:ring-destructive/30",
      },
    },
  }
)

function Button({
  size,
  type = "button",
  variant,
  className,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      type={type}
      data-slot="button"
      className={cn(buttonVariants({ size, variant, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
